import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  alpha,
  Button,
  CircularProgress,
  TextField,
  Badge,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Menu as MenuIcon,
  Send as SendIcon,
  Image as ImageIcon,
  AttachFile as AttachFileIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Visibility as VisibilityIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { friendsAPI, chatAPI, notificationAPI, getBookAssetUrl } from '../services/api';
import { resolveNotificationUrl } from '../services/notificationNavigation';

const FRIENDS_PAGE_SIZE = 20;
/**
 * Chat page layout:
 * - Large screen: left column = conversation list, center = chat, right column = friend list
 * - Small screen: main = chatbox only; conversation list in LEFT drawer, friends in RIGHT drawer (no long list above chat)
 */
const Chat = () => {
  const [searchParams] = useSearchParams();
  const withUserId = searchParams.get('with');
  const majorParam = searchParams.get('major');
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { isAuthenticated, loading: authLoading, user: authUser, logout } = useAuth();

  // Profile menu state
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [conversationMenuAnchorEl, setConversationMenuAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendsPage, setFriendsPage] = useState(1);
  const [friendsHasMore, setFriendsHasMore] = useState(false);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [friendsLoadingMore, setFriendsLoadingMore] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const previousMessagesCountRef = useRef(0);
  const latestMessageIdRef = useRef(null);
  const [messageText, setMessageText] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [mobileConversationsDrawerOpen, setMobileConversationsDrawerOpen] = useState(false);
  const [mobileFriendsDrawerOpen, setMobileFriendsDrawerOpen] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedByMe, setBlockedByMe] = useState(false);
  const [blockedByOther, setBlockedByOther] = useState(false);
  const fileInputRef = useRef(null);

  // New state for Requests and Suggestions
  const [activeTab, setActiveTab] = useState(0); // 0: Friends, 1: Requests, 2: Discover
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  const myId = authUser?.id != null ? Number(authUser.id) : null;
  // Determine major from URL parameter or default to 'english'
  // Valid majors: english, korea, chinese, japanese, russian
  const validMajors = ['english', 'korea', 'chinese', 'japanese', 'russian'];
  const CHAT_MAJOR = majorParam && validMajors.includes(majorParam.toLowerCase()) 
    ? majorParam.toLowerCase() 
    : 'english';

  const getMessageTimestamp = useCallback((msg) => {
    const rawTimestamp = msg?.createdAt ?? msg?.created_at ?? null;
    if (rawTimestamp == null) return 0;

    if (typeof rawTimestamp === 'number') {
      return rawTimestamp > 1e12 ? rawTimestamp : rawTimestamp * 1000;
    }

    const parsed = new Date(rawTimestamp).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const normalizeMessages = useCallback((items = []) => (
    Array.from(new Map(items.map((msg) => [msg.id, msg])).values())
      .sort((a, b) => {
        const timeDiff = getMessageTimestamp(a) - getMessageTimestamp(b);
        if (timeDiff !== 0) return timeDiff;
        return Number(a.id) - Number(b.id);
      })
  ), [getMessageTimestamp]);

  // Fetch notifications
  useEffect(() => {
    if (isAuthenticated && authUser) {
      const fetchNotifications = async () => {
        try {
          const result = await notificationAPI.get();
          // Handle both array response and object response with notifications property
          const notifs = Array.isArray(result.data) ? result.data : (result.data?.notifications || []);
          const unread = Array.isArray(result.data) 
            ? result.data.filter((n) => !n.seen).length 
            : (result.data?.unreadCount || notifs.filter((n) => !n.seen).length);
            
          setNotifications(notifs);
          setUnreadCount(unread);
        } catch (err) {
          console.error('Failed to fetch notifications:', err);
        }
      };
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, authUser]);

  // Format relative time for notifications
  const formatTimeAgo = (timestamp) => {
    const normalized =
      typeof timestamp === 'number'
        ? timestamp
        : Math.floor(new Date(timestamp).getTime() / 1000);
    if (!normalized || Number.isNaN(normalized)) return '';
    const now = Math.floor(Date.now() / 1000);
    const diff = now - normalized;
  
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(normalized * 1000).toLocaleDateString();
  };

  const isUnreadNotification = (notif) => notif?.readAt == null && notif?.seen !== 1;

  // Profile menu handlers
  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationClick = async (notif) => {
    if (isUnreadNotification(notif)) {
      try {
        await notificationAPI.markOneRead(notif.id);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, seen: 1, readAt: n.readAt || new Date().toISOString() } : n))
        );
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    handleNotificationClose();
    const url = resolveNotificationUrl(notif);
    if (url) navigate(url);
  };

  // Conversation menu handlers
  const handleConversationMenuOpen = (event) => {
    setConversationMenuAnchorEl(event.currentTarget);
  };

  const handleConversationMenuClose = () => {
    setConversationMenuAnchorEl(null);
  };

  const handleViewProfile = () => {
    handleConversationMenuClose();
    // Get friend ID from either currentConversation.friend.id or friend.phone if that's what's available
    const friendId = currentConversation?.friendId;
    if (friendId) {
      navigate(`/profile/${friendId}`);
    } else {
      console.error(currentConversation);
    }
  };

  const handleDeleteConversation = async () => {
    handleConversationMenuClose();
    if (!currentConversation?.id) return;
    
    if (!window.confirm('Are you sure you want to delete this conversation? All messages will be permanently deleted.')) {
      return;
    }

    try {
      await chatAPI.deleteConversation(currentConversation.id);
      // Remove conversation from list
      setConversations((prev) => prev.filter((c) => c.id !== currentConversation.id));
      // Clear current conversation
      setCurrentConversation(null);
      setMessages([]);
      // Refresh conversations list
      fetchConversations(false);
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const handleBlockUser = async () => {
    handleConversationMenuClose();
    if (!currentConversation?.friendId) return;
    
    if (!window.confirm(`Are you sure you want to block ${currentConversation.friend.name || 'this user'}? You will no longer receive messages from them.`)) {
      return;
    }

    try {
      // Block the user (adds to blocks table and unfriends)
      await friendsAPI.block(currentConversation.friendId);
      // Delete the conversation
      if (currentConversation?.id) {
        try {
          await chatAPI.deleteConversation(currentConversation.id);
        } catch (err) {
          console.error('Failed to delete conversation after blocking:', err);
        }
      }
      // Remove conversation from list
      setConversations((prev) => prev.filter((c) => c.id !== currentConversation.id));
      // Clear current conversation
      setCurrentConversation(null);
      setMessages([]);
      // Refresh conversations and friends lists
      fetchConversations(false);
      fetchFriends();
      // Update block status
      setIsBlocked(true);
      setBlockedByMe(true);
      setBlockedByOther(false);
      alert('User has been blocked successfully.');
    } catch (err) {
      console.error('Failed to block user:', err);
      alert('Failed to block user. Please try again.');
    }
  };

  const handleUnblockUser = async () => {
    if (!currentConversation?.friend?.id) return;
    
    if (!window.confirm(`Are you sure you want to unblock ${currentConversation.friend.name || 'this user'}? You will be able to send and receive messages again.`)) {
      return;
    }

    try {
      // Unblock the user
      await friendsAPI.unblock(currentConversation.friend.id);
      // Update block status
      setIsBlocked(false);
      setBlockedByMe(false);
      setBlockedByOther(false);
      // Refresh conversations to get updated block status
      fetchConversations(false);
      alert('User has been unblocked successfully.');
    } catch (err) {
      console.error('Failed to unblock user:', err);
      alert('Failed to unblock user. Please try again.');
    }
  };

  // Fetch Requests
  const fetchRequests = useCallback(async () => {
    if (!authUser?.id) return;
    setRequestsLoading(true);
    try {
      const res = await friendsAPI.getRequests();
      const list = Array.isArray(res.data) ? res.data : [];
      setRequests(list);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    } finally {
      setRequestsLoading(false);
    }
  }, [authUser?.id]);

  // Fetch Suggestions
  const fetchSuggestions = useCallback(async () => {
    if (!authUser?.id) return;
    setSuggestionsLoading(true);
    try {
      const res = await friendsAPI.getPeopleYouMayKnow(1, 20);
      const list = Array.isArray(res.data) ? res.data : [];
      setSuggestions(list);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [authUser?.id]);

  const handleAcceptRequest = async (userId) => {
    try {
      await friendsAPI.confirm(userId);
      setRequests((prev) => prev.filter((r) => r.userId !== userId));
      fetchFriends(1, false);
    } catch (err) {
      console.error('Failed to accept request:', err);
      alert('Failed to accept request.');
    }
  };

  const handleDeclineRequest = async (userId) => {
    try {
      await friendsAPI.removeRequest(userId);
      setRequests((prev) => prev.filter((r) => r.userId !== userId));
    } catch (err) {
      console.error('Failed to decline request:', err);
      alert('Failed to decline request.');
    }
  };

  const handleAddFriend = async (userId) => {
    try {
      await friendsAPI.addRequest(userId);
      setSuggestions((prev) => prev.filter((s) => s.userId !== userId));
      setSearchResults((prev) => prev.map((s) => s.userId === userId ? { ...s, status: 'pending_sent' } : s));
      alert('Friend request sent!');
    } catch (err) {
      console.error('Failed to send request:', err);
      alert('Failed to send request.');
    }
  };

  // Handle Search
  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await friendsAPI.search(query);
        const list = Array.isArray(res.data) ? res.data : [];
        setSearchResults(list);
      } catch (err) {
        console.error('Failed to search friends:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  };

  // Fetch conversations
  const fetchConversations = useCallback(async (showLoading = false) => {
    if (!authUser?.id) return;
    if (showLoading) setConversationsLoading(true);
    try {
      const res = await chatAPI.getConversations();
      const list = (res.data || [])
        // Filter out admin support conversations (friend id 10000)
        // Handle both string and number comparison
        .filter((c) => {
          const friendPhone = c.friend?.phone;
          const friendPhoneNum = Number(friendPhone);
          return friendPhoneNum !== 10000; // Hide admin support conversations
        })
        .map((c) => ({
          id: c.id,
          name: c.friend?.name ?? 'Unknown',
          image: c.friend?.image,
          preview: c.lastMessageText || 'No messages yet',
          friend: c.friend,
          friendId: Number(c.user1Id === authUser.id ? c.user2Id : c.user1Id),
          unreadCount: c.unreadCount || 0,
        }));
      setConversations(list);
      
      // Update block status if current conversation is in the list
      if (currentConversation?.id) {
        const currentConv = list.find((c) => c.id === currentConversation.id);
        if (currentConv?.friend) {
          setIsBlocked(currentConv.friend.blocked || false);
          setBlockedByMe(currentConv.friend.blocked_by_me || false);
          setBlockedByOther(currentConv.friend.blocked_by_other || false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      // Don't clear conversations on error during polling
      if (showLoading) setConversations([]);
    } finally {
      if (showLoading) setConversationsLoading(false);
    }
  }, [authUser?.id, currentConversation?.id]);

  const fetchFriends = useCallback(async (page = 1, append = false) => {
    if (!authUser?.id) return;
    const isLoadMore = append && page > 1;
    if (isLoadMore) setFriendsLoadingMore(true);
    else setFriendsLoading(true);
    try {
      const res = await friendsAPI.getFriends(page, FRIENDS_PAGE_SIZE);
      const list = res.data || [];
      const pagination = res.pagination || {};
      if (append) {
        setFriends((prev) => [...prev, ...list]);
      } else {
        setFriends(list);
      }
      setFriendsPage(page);
      setFriendsHasMore(!!pagination.hasMore);
    } catch (err) {
      console.error('Failed to fetch friends:', err);
      if (!append) setFriends([]);
    } finally {
      setFriendsLoading(false);
      setFriendsLoadingMore(false);
    }
  }, [authUser?.id]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: '/chat' }, replace: true });
      return;
    }
    if (isAuthenticated && authUser?.id) {
      fetchFriends(1, false);
      fetchConversations(true); // Show loading on initial fetch
      fetchRequests();
      fetchSuggestions();
      
      const lastPollAtRef = { current: Date.now() };
      const conversationsPollInterval = setInterval(() => {
        const now = Date.now();
        const intervalMs = document.hidden || !document.hasFocus() ? 30000 : 15000;
        if (now - lastPollAtRef.current < intervalMs) return;
        lastPollAtRef.current = now;
        fetchConversations(false);
      }, 5000);
      
      return () => {
        clearInterval(conversationsPollInterval);
      };
    }
  }, [authLoading, isAuthenticated, authUser?.id, navigate, fetchFriends, fetchConversations]);

  // Open conversation from URL ?with=userId&major=language
  useEffect(() => {
    if (!withUserId || !myId || !isAuthenticated) return;
    const friendId = Number(withUserId);
    if (friendId === myId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await chatAPI.createConversation(myId, friendId, 'web');
        if (cancelled) return;
        const conv = res.data;
        // Use friend data from API response, fallback to friends list or default
        
        const friendData = conv.friend || (() => {
          const otherId = conv.user1Id === myId ? conv.user2Id : conv.user1Id;
          const friendFromList = friends.find((f) => Number(f.userId || f.phone) === otherId);
          return friendFromList
            ? { name: friendFromList.userName, image: friendFromList.userImage, phone: friendFromList.phone, id: otherId }
            : { name: `User ${otherId}`, image: null, phone: null, id: otherId };
        })();
        setCurrentConversation({
          id: conv.id,
          friendId: friendId,
          friend: friendData,
        });
        
        // Update block status from conversation data
        if (friendData.blocked !== undefined) {
          setIsBlocked(friendData.blocked || false);
          setBlockedByMe(friendData.blockedByMe || false);
          setBlockedByOther(friendData.blockedByOther || false);
        }
        
        // Clean up URL - remove query params after conversation is created
        navigate('/chat', { replace: true });
      } catch (err) {
        console.error('Failed to open conversation:', err);
        // Clean up URL immediately to stop loading spinner
        navigate('/chat', { replace: true });
        setCurrentConversation(null);
        // Show user-friendly error message after navigation
        const errorMessage = err?.message || err?.error || 'Failed to start conversation. The support user may not exist or there was a database error.';
        alert(`Unable to start chat: ${errorMessage}`);
      }
    })();
    return () => { cancelled = true; };
  }, [withUserId, myId, isAuthenticated, CHAT_MAJOR]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check block status when conversation changes
  useEffect(() => {
    if (!currentConversation?.friend?.phone || !myId) {
      setIsBlocked(false);
      setBlockedByMe(false);
      setBlockedByOther(false);
      return;
    }

    // Use block status from conversation data if available (from API)
    if (currentConversation.friend.blocked !== undefined) {
      setIsBlocked(currentConversation.friend.blocked || false);
      setBlockedByMe(currentConversation.friend.blocked_by_me || false);
      setBlockedByOther(currentConversation.friend.blocked_by_other || false);
      return;
    }

    // Otherwise, check block status via API
    const checkBlockStatus = async () => {
      try {
        const result = await friendsAPI.checkBlock(currentConversation.friend.phone);
        if (result && result.data) {
          setIsBlocked(result.data.blocked || false);
          setBlockedByMe(result.data.blocked_by_me || false);
          setBlockedByOther(result.data.blocked_by_other || false);
        }
      } catch (err) {
        console.error('Failed to check block status:', err);
        setIsBlocked(false);
        setBlockedByMe(false);
        setBlockedByOther(false);
      }
    };

    checkBlockStatus();
  }, [currentConversation?.friend?.phone, currentConversation?.friend?.blocked, myId]);

  // Fetch messages function (can be called manually or by polling)
  const fetchMessages = useCallback(async (showLoading = true, beforeId = null, afterId = null) => {
    if (!currentConversation?.id || !myId) {
      setMessages([]);
      setHasMoreMessages(true);
      latestMessageIdRef.current = null;
      return;
    }
    
    const isLoadingOlder = beforeId !== null;
    // If afterId is null but showLoading is false, it's polling - use latest message ID
    const isLoadingNewer = afterId !== null || (!showLoading && !isLoadingOlder && latestMessageIdRef.current !== null);
    const actualAfterId = afterId || (isLoadingNewer && !afterId ? latestMessageIdRef.current : null);
    
    if (isLoadingOlder) {
      setLoadingOlderMessages(true);
    } else if (showLoading) {
      setMessagesLoading(true);
    }
    
    try {
      const params = { limit: 50 };
      if (beforeId) {
        params.before_id = beforeId;
      } else if (actualAfterId) {
        params.after_id = actualAfterId;
      }
      // chatAPI.getMessages signature: (conversationId, major, params)
      const res = await chatAPI.getMessages(currentConversation.id, CHAT_MAJOR, params);
      const newMessages = res.data || [];
      const normalizedNewMessages = normalizeMessages(newMessages);
      
      if (isLoadingOlder) {
        // Prepend older messages to existing messages
        if (normalizedNewMessages.length > 0) {
          setMessages((prev) => normalizeMessages([...normalizedNewMessages, ...prev]));
          // If we got fewer messages than requested, there are no more
          if (newMessages.length < 50) {
            setHasMoreMessages(false);
          }
        } else {
          setHasMoreMessages(false);
        }
      } else if (isLoadingNewer && actualAfterId) {
        // Append newer messages to existing messages (for polling)
        if (normalizedNewMessages.length > 0) {
          setMessages((prev) => {
            // Create a map to avoid duplicates
            const messageMap = new Map();
            // Add existing messages to map
            prev.forEach((msg) => messageMap.set(msg.id, msg));
            // Add new messages to map (will overwrite if duplicate)
            normalizedNewMessages.forEach((msg) => messageMap.set(msg.id, msg));
            // Convert back to array and sort in chronological order
            const sorted = normalizeMessages(Array.from(messageMap.values()));
            // Update latest message ID ref
            if (sorted.length > 0) {
              latestMessageIdRef.current = sorted[sorted.length - 1].id;
            }
            return sorted;
          });
          chatAPI.markRead(currentConversation.id, myId).catch(() => {});
        }
      } else {
        // Replace all messages (initial load only)
        setMessages(normalizedNewMessages);
        // Update latest message ID ref
        if (normalizedNewMessages.length > 0) {
          latestMessageIdRef.current = normalizedNewMessages[normalizedNewMessages.length - 1].id;
        } else {
          latestMessageIdRef.current = null;
        }
        // If we got fewer messages than requested, there are no more
        if (newMessages.length < 50) {
          setHasMoreMessages(false);
        } else {
          setHasMoreMessages(true);
        }
        if (newMessages.length > 0) {
          chatAPI.markRead(currentConversation.id, myId).catch(() => {});
        }
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      // Don't clear messages on error during polling
      if (showLoading && !isLoadingOlder && !isLoadingNewer) {
        setMessages([]);
      }
      if (!isLoadingNewer) {
        setHasMoreMessages(false);
      }
    } finally {
      if (isLoadingOlder) {
        setLoadingOlderMessages(false);
      } else if (showLoading) {
        setMessagesLoading(false);
      }
    }
  }, [currentConversation?.id, myId, CHAT_MAJOR, normalizeMessages]);

  const previousConversationsRef = useRef([]);

  // Check for new unread messages in conversations list (for background conversations)
  useEffect(() => {
    if (conversations.length > 0 && previousConversationsRef.current.length > 0) {
      conversations.forEach((conv) => {
        const prevConv = previousConversationsRef.current.find((p) => p.id === conv.id);
        // If unread count increased
        if (prevConv && conv.unreadCount > prevConv.unreadCount) {
          // If this is NOT the current conversation (which is handled by messages effect), notify
          if (currentConversation?.id !== conv.id) {
            if ('Notification' in window && Notification.permission === 'granted' && (document.hidden || !document.hasFocus())) {
              try {
                new Notification(conv.name, {
                  body: conv.preview || 'New message',
                  icon: conv.image || '/logo.png',
                  tag: `chat-${conv.id}`,
                });
              } catch (e) {
                console.error('Notification error:', e);
              }
            }
          }
        }
      });
    }
    previousConversationsRef.current = conversations;
  }, [conversations, currentConversation]);

  // Initial fetch and polling setup for messages
  useEffect(() => {
    if (!currentConversation?.id || !myId) {
      setMessages([]);
      setHasMoreMessages(true);
      isInitialLoadRef.current = true;
      previousMessagesCountRef.current = 0;
      return;
    }
    
    // Reset initial load flag when conversation changes
    isInitialLoadRef.current = true;
    previousMessagesCountRef.current = 0;
    setHasMoreMessages(true);
    latestMessageIdRef.current = null;
    
    // Initial fetch
    fetchMessages(true);
    
    // Set up polling
    const pollInterval = setInterval(() => {
      if (document.hidden || !document.hasFocus()) return;
      fetchMessages(false);
    }, 7000);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [currentConversation?.id, myId, fetchMessages]);

  // Handle scroll to load older messages
  const handleScroll = useCallback((e) => {
    const container = e.target;
    // Check if scrolled to top (within 100px)
    if (container.scrollTop < 100 && hasMoreMessages && !loadingOlderMessages && messages.length > 0) {
      // Get the oldest message ID (first message in array)
      const oldestMessageId = messages[0]?.id;
      if (oldestMessageId) {
        // Save current scroll height and position
        const scrollHeightBefore = container.scrollHeight;
        const scrollTopBefore = container.scrollTop;
        
        // Load older messages
        fetchMessages(false, oldestMessageId).then(() => {
          // Restore scroll position after messages are loaded
          // Calculate the difference in scroll height and adjust scroll position
          requestAnimationFrame(() => {
            const scrollHeightAfter = container.scrollHeight;
            const scrollHeightDiff = scrollHeightAfter - scrollHeightBefore;
            container.scrollTop = scrollTopBefore + scrollHeightDiff;
          });
        });
      }
    }
  }, [hasMoreMessages, loadingOlderMessages, messages, fetchMessages]);

  // Keep latest message ID ref in sync
  useEffect(() => {
    if (messages.length > 0) {
      latestMessageIdRef.current = messages[messages.length - 1].id;
    } else {
      latestMessageIdRef.current = null;
    }
  }, [messages]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Improved auto-scroll and browser notifications: instant on initial load, smooth for new messages
  useEffect(() => {
    // Don't scroll while loading
    if (messagesLoading) return;
    
    if (!messagesEndRef.current || messages.length === 0) return;
    
    const currentMessagesCount = messages.length;
    const isNewMessage = currentMessagesCount > previousMessagesCountRef.current;
    const wasInitialLoad = isInitialLoadRef.current;
    
    // Browser notification for new message
    if (isNewMessage && !wasInitialLoad) {
      const lastMsg = messages[messages.length - 1];
      // Only notify if message is not from me and window is hidden or user is on another tab
      if (lastMsg && lastMsg.senderId !== myId) {
        if (document.hidden || !document.hasFocus()) {
          if ('Notification' in window && Notification.permission === 'granted') {
            const senderName = currentConversation?.friend?.name || 'New Message';
            const msgBody = lastMsg.messageType === 'image' ? 'Sent an image' : (lastMsg.messageText || 'New message');
            
            try {
              new Notification(senderName, {
                body: msgBody,
                icon: currentConversation?.friend?.image || '/logo.png', // Fallback to logo if no image
                tag: `chat-${currentConversation?.id}`, // Replace existing notification for same chat
              });
            } catch (e) {
              console.error('Notification error:', e);
            }
          }
        }
      }
    }

    // Use double requestAnimationFrame to ensure DOM is fully updated and rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (messagesEndRef.current && !messagesLoading) {
          if (wasInitialLoad) {
            // Instant scroll on initial load - no animation, better UX
            // Use scrollTop for instant positioning instead of scrollIntoView
            const container = messagesContainerRef.current;
            if (container) {
              container.scrollTop = container.scrollHeight;
            } else {
              messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
            }
            isInitialLoadRef.current = false;
          } else if (isNewMessage) {
            // Smooth scroll only when new messages arrive
            // Check if user is near bottom before auto-scrolling
            const container = messagesContainerRef.current;
            if (container) {
              const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
              if (isNearBottom) {
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            } else {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }
          previousMessagesCountRef.current = currentMessagesCount;
        }
      });
    });
  }, [messages, messagesLoading]);

  const handleLoadMoreFriends = () => {
    if (friendsLoadingMore || !friendsHasMore) return;
    fetchFriends(friendsPage + 1, true);
  };

  const hasConversations = conversations.length > 0;
  const hasFriends = friends.length > 0;

  // Render Search Result Item
  const renderSearchResultItem = (user) => {
    let action = null;
    
    // Determine action button based on status
    if (user.status === 'friend') {
      action = (
        <Button size="small" disabled startIcon={<CheckIcon />}>
          Friend
        </Button>
      );
    } else if (user.status === 'pending_sent') {
      action = (
        <Button size="small" disabled>
          Sent
        </Button>
      );
    } else if (user.status === 'pending_received') {
      action = (
        <Button size="small" variant="contained" onClick={() => handleAcceptRequest(user.userId)}>
          Accept
        </Button>
      );
    } else {
      action = (
        <IconButton edge="end" color="primary" onClick={() => handleAddFriend(user.userId)} size="small">
          <PersonAddIcon />
        </IconButton>
      );
    }

    return (
      <ListItem 
        key={user.userId} 
        disablePadding
        secondaryAction={action}
      >
        <ListItemButton sx={{ px: 2, py: 1.5, cursor: 'default' }}>
          <ListItemAvatar>
            <Avatar src={user.userImage} alt={user.userName}>{(user.userName || '?').charAt(0)}</Avatar>
          </ListItemAvatar>
          <ListItemText 
            primary={user.userName} 
            secondary={user.mutualFriends ? `Mutual Friends: ${user.mutualFriends}` : 'No mutual friends'}
            primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
            secondaryTypographyProps={{ fontSize: '0.75rem' }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const handleSelectConversation = (conv) => {
    // Ensure we have a valid friend ID. If conv.friend.id is missing, try to deduce it from user IDs.
    const friendId = conv.friendId;
    console.log(conv);
    const friendData = conv.friend ? { ...conv.friend, id: friendId } : { 
      name: conv.name, 
      image: conv.image, 
      phone: null, 
      id: friendId 
    };
    
    setCurrentConversation({ id: conv.id,friendId: friendId, friend: friendData });
    
    // Update block status from conversation data
    if (friendData.blocked !== undefined) {
      setIsBlocked(friendData.blocked || false);
      setBlockedByMe(friendData.blocked_by_me || false);
      setBlockedByOther(friendData.blocked_by_other || false);
    }
    
    // Clear unread count for this conversation immediately (optimistic update)
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conv.id ? { ...c, unreadCount: 0 } : c
      )
    );
    
    if (!isDesktop) setMobileConversationsDrawerOpen(false);
  };

  const handleSelectFriend = async (friendId) => {
    if (!myId || !friendId) return;
    if (!isDesktop) setMobileFriendsDrawerOpen(false);
    const fid = Number(friendId);
    try {
      const res = await chatAPI.createConversation(myId, fid, 'web');
      const conv = res.data;
      // Use friend data from API response, fallback to friends list or default
      const friendData = conv.friend || (() => {
        const friendFromList = friends.find((f) => Number(f.userId || f.phone) === fid);
        return friendFromList
          ? { name: friendFromList.userName, image: friendFromList.userImage, phone: friendFromList.phone, id: fid }
          : { name: `User ${fid}`, image: null, phone: null, id: fid };
      })();
      setCurrentConversation({
        id: conv.id,
        friendId: fid,
        friend: friendData,
      });
      
      // Update block status from conversation data
      if (friendData.blocked !== undefined) {
        setIsBlocked(friendData.blocked || false);
        setBlockedByMe(friendData.blocked_by_me || false);
        setBlockedByOther(friendData.blocked_by_other || false);
      }
      
      fetchConversations(false); // Don't show loading spinner when refreshing after action
    } catch (err) {
      console.error('Failed to create conversation:', err);
    }
  };

  const handleSendMessage = async (imagePath = null, imageSize = null) => {
    const text = messageText.trim();
    if ((!text && !imagePath) || !currentConversation?.id || !myId || sendLoading || uploadingImage) return;
    setSendLoading(true);
    const optimistic = {
      id: `temp-${Date.now()}`,
      conversationId: currentConversation.id,
      senderId: myId,
      messageType: imagePath ? 'image' : 'text',
      messageText: text || '',
      filePath: imagePath || '',
      fileSize: imageSize || 0,
      isRead: 0,
      createdAt: Date.now(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setMessageText('');
    try {
      // In normal chat, major is not strictly required but we can default to 'english' if needed, or null.
      // However, previous implementation passed payload as 3rd arg, but now 3rd arg is major.
      // So we must pass major (e.g. 'english') as 3rd arg, and payload as 4th arg.
      // Or pass null if major is not relevant for user-to-user chat.
      // The error "major [object Object]" suggests the payload was passed as the 3rd arg (major).
      
      const res = await chatAPI.sendMessage(currentConversation.id, myId, 'web', {
        message_text: text,
        message_type: imagePath ? 'image' : 'text',
        file_path: imagePath || '',
        file_size: imageSize || 0,
      });
      const created = res.data;
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? created : m)));
      fetchConversations(false); // Don't show loading spinner when refreshing after sending message
      // Scroll to bottom after sending message (will be handled by useEffect, but ensure it happens)
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setMessageText(text);
      console.error('Failed to send message:', err);
    } finally {
      setSendLoading(false);
    }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadRes = await chatAPI.uploadImage(file);
      if (uploadRes.success && uploadRes.data) {
        const { filePath, fileSize, fileUrl } = uploadRes.data;
        // Send the image message (filePath is stored in DB, fileUrl is for display)
        await handleSendMessage(filePath, fileSize);
      } else {
        alert(uploadRes.error || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const formatMessageTime = (ts) => {
    if (!ts) return '';
    const d = new Date(typeof ts === 'number' ? ts : ts * 1000);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box
      sx={{
        height: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header — same design as main app Navbar */}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          minHeight: { xs: 56, sm: 60 },
          borderRadius: 0,
          borderBottom: '1px solid',
          borderColor: mode === 'light' ? alpha('#000', 0.08) : alpha('#fff', 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1301,
          backgroundColor: mode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(15, 23, 42, 0.8)',
          backdropFilter: 'blur(20px)',
          color: 'text.primary',
        }}
      >
        {!isDesktop ? (
          <>
            <IconButton
              data-testid="chat-open-conversations"
              onClick={() => setMobileConversationsDrawerOpen(true)}
              sx={{ 
                mr: { xs: 0, sm: 0.5 },
                padding: 1,
                bgcolor: alpha(theme.palette.text.primary, 0.05),
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.1) },
                '& svg': { fontSize: '1.25rem' },
              }}
              aria-label="Open conversations"
            >
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              src={`https://www.calamuseducation.com/uploads/profiles/calamus.png`}
              alt="Calamus Education"
              onClick={() => navigate('/')}
              sx={{
                height: { xs: 28, sm: 36 },
                width: { xs: 28, sm: 'auto' },
                borderRadius: { xs: '50%', sm: 1 },
                cursor: 'pointer',
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0,
                mx: 1,
              }}
            />
            <Typography 
              variant="h6" 
              fontWeight={700} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                flex: 1,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
              }}
            >
              <ChatIcon color="primary" sx={{ fontSize: { xs: 20, md: 24 } }} />
              Chat
            </Typography>
            {isAuthenticated && (
              <>
                <IconButton
                  color="inherit"
                  onClick={handleNotificationOpen}
                  aria-label="notifications"
                  size="small"
                  sx={{ 
                    mr: 0.5,
                    padding: 1,
                    bgcolor: alpha(theme.palette.text.primary, 0.05),
                    '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.1) },
                  }}
                >
                  <Badge badgeContent={unreadCount} color="secondary" max={99} sx={{ '& .MuiBadge-badge': { border: '2px solid', borderColor: 'background.paper', fontWeight: 700, fontSize: '0.65rem' } }}>
                    <NotificationsIcon sx={{ fontSize: '1.25rem' }} />
                  </Badge>
                </IconButton>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  sx={{ 
                    p: 0.25,
                    border: '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  <Avatar
                    alt={authUser?.name || 'User'}
                    src={authUser?.image}
                    sx={{ width: { xs: 32, sm: 34 }, height: { xs: 32, sm: 34 }, boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
                  />
                </IconButton>
              </>
            )}
            <IconButton
              data-testid="chat-open-friends"
              onClick={() => setMobileFriendsDrawerOpen(true)}
              sx={{ 
                ml: 1,
                padding: 1,
                bgcolor: alpha(theme.palette.text.primary, 0.05),
                '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.1) },
                '& svg': { fontSize: '1.25rem' },
              }}
              aria-label="Open friends list"
            >
              <PeopleIcon />
            </IconButton>
          </>
        ) : (
          <>
            <Box
              component="img"
              src={`https://www.calamuseducation.com/uploads/profiles/calamus.png`}
              alt="Calamus Education"
              onClick={() => navigate('/')}
              sx={{
                height: { xs: 28, sm: 36 },
                width: { xs: 28, sm: 'auto' },
                borderRadius: { xs: '50%', sm: 1 },
                cursor: 'pointer',
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0,
                mr: 2,
              }}
            />
            <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, fontSize: '1.1rem' }}>
              <ChatIcon color="primary" />
              Chat
            </Typography>
            {isAuthenticated && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => navigate('/my-learning')}
                  sx={{ 
                    display: { xs: 'none', md: 'flex' },
                    mr: 1,
                    borderRadius: '8px',
                    px: 2,
                    py: 0.5,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: `0 4px 12px ${alpha('#2e7d32', 0.2)}`,
                  }}
                >
                  My Learning
                </Button>
                <IconButton
                  color="inherit"
                  onClick={handleNotificationOpen}
                  aria-label="notifications"
                  size="small"
                  sx={{ padding: 1, bgcolor: alpha(theme.palette.text.primary, 0.05), '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.1) } }}
                >
                  <Badge badgeContent={unreadCount} color="secondary" max={99} sx={{ '& .MuiBadge-badge': { border: '2px solid', borderColor: 'background.paper', fontWeight: 700, fontSize: '0.65rem' } }}>
                    <NotificationsIcon sx={{ fontSize: '1.25rem' }} />
                  </Badge>
                </IconButton>
                <IconButton
                  onClick={handleProfileMenuOpen}
                  size="small"
                  sx={{ 
                    p: 0.25,
                    border: '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  <Avatar
                    alt={authUser?.name || 'User'}
                    src={authUser?.image}
                    sx={{ width: { xs: 32, sm: 34 }, height: { xs: 32, sm: 34 }, boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
                  />
                </IconButton>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            width: { xs: 300, sm: 360 },
            maxHeight: 440,
            borderRadius: 2,
            boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
            border: 'none',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon fontSize="small" />}
                onClick={async () => {
                   try {
                     await notificationAPI.markRead();
                     setUnreadCount(0);
                     setNotifications((prev) => prev.map((n) => ({ ...n, seen: 1 })));
                   } catch (err) {
                     console.error('Failed to mark notifications as read:', err);
                   }
                }}
                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}
              >
                Mark all read
              </Button>
            )}
            <Button
              size="small"
              onClick={() => { handleNotificationClose(); navigate('/notifications'); }}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              View all
            </Button>
          </Box>
        </Box>
        <Divider />

        <Box sx={{ overflowY: 'auto', maxHeight: 360 }}>
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          notifications.map((notif) => {
            const actorName = notif?.actor?.name || notif?.writer?.name || 'Someone';
            const actorImage = notif?.actor?.image || notif?.writer?.image || notif?.writerImage;
            const type = String(notif?.type || '');
            const targetType = String(notif?.target?.targetType || notif?.target?.type || '').toLowerCase();
            const actionText =
              notif?.actionText ||
              (() => {
                if (type === 'comment.like') {
                  if (targetType === 'lesson' || notif?.target?.courseId || notif?.metadata?.courseId) {
                    return 'liked your comment on a lesson';
                  }
                  return 'liked your comment';
                }
                return (
                  {
                    'post.like': 'liked your post',
                    'post.comment': 'commented on your post',
                    'comment.created': 'commented on your post',
                    'post.comment_reply': 'replied to your comment',
                    'comment.reply': 'replied to your comment',
                    'lesson.added': 'added a new lesson',
                    'lesson.comment': 'commented on a lesson',
                    'lesson.comment_reply': 'replied to your lesson comment',
                    'payment.created': 'submitted a payment',
                    'payment.activated': 'activated your subscription',
                    'chat.message': 'sent you a message',
                    'chat.support': 'sent a support message',
                    'friend.request': 'sent you a friend request',
                    'friend.accept': 'accepted your friend request',
                  }[type] || 'sent you a notification'
                );
              })();

            const subtitleText =
              notif?.target?.title ||
              (() => {
                if (type === 'chat.message') return 'Tap to open chat';
                if (type === 'payment.activated') return 'Tap to open My Learning';
                if (type === 'payment.created') return 'Tap to view payment';
                if (type.startsWith('lesson.') || (type === 'comment.like' && (notif?.target?.courseId || notif?.metadata?.courseId))) {
                  return 'Tap to view lesson';
                }
                if (type.startsWith('friend.')) return 'Tap to view profile';
                if (type.startsWith('post.') || type.startsWith('comment.')) return 'Tap to view post';
                return null;
              })();
            const unread = isUnreadNotification(notif);
            const notifTime = notif?.createdAt || notif?.time;
            return (
            <MenuItem
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              sx={{
                py: 1.5,
                px: 2,
                bgcolor: unread ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                borderLeft: unread ? '3px solid' : 'none',
                borderColor: 'primary.main',
                whiteSpace: 'normal',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                <Avatar
                  src={actorImage}
                  sx={{ width: 40, height: 40, flexShrink: 0 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    <Typography component="span" variant="body2" fontWeight={600}>
                      {actorName}
                    </Typography>{' '}
                    {actionText}
                  </Typography>
                  {subtitleText && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {subtitleText}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.3, display: 'block' }}>
                    {formatTimeAgo(notifTime)}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          );
          })
        )}
        </Box>
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {authUser?.name || 'User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {authUser?.email || authUser?.id || ''}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          View Profile
        </MenuItem>
        <MenuItem onClick={toggleTheme}>
          <ListItemIcon>
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </ListItemIcon>
          {mode === 'dark' ? 'Light Mode' : 'Night Mode'}
        </MenuItem>
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { logout && logout(); navigate('/'); }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: 'hidden',
          maxWidth: 1600,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Left column: Conversation list (desktop only; on mobile it's in a left drawer) */}
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'none', md: 'flex' },
            width: 320,
            minWidth: 320,
            maxWidth: 320,
            borderRight: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ 
            px: 2.5, 
            py: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
              Conversations
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {conversationsLoading ? 'Loading…' : hasConversations ? `${conversations.length} chat(s)` : 'Your chats will appear here'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
            {conversationsLoading && conversations.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : !hasConversations ? (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <MessageIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.6 }} />
                </Box>
                <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
                  No conversations yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                  Tap a friend to start a chat
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {conversations.map((c) => {
                  const hasUnread = (c.unreadCount || 0) > 0;
                  return (
                    <ListItem key={c.id} disablePadding>
                      <ListItemButton
                        selected={currentConversation?.id === c.id}
                        onClick={() => handleSelectConversation(c)}
                        sx={{
                          bgcolor: hasUnread && currentConversation?.id !== c.id 
                            ? alpha(theme.palette.primary.main, 0.08) 
                            : 'transparent',
                          '&:hover': {
                            bgcolor: hasUnread && currentConversation?.id !== c.id
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.action.hover, 0.05),
                          },
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 48 }}>
                          <Badge 
                            badgeContent={hasUnread ? c.unreadCount : 0} 
                            color="primary" 
                            max={99}
                            invisible={!hasUnread}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                minWidth: 18,
                                height: 18,
                              },
                            }}
                          >
                            <Avatar 
                              src={c.image} 
                              alt={c.name}
                              sx={{
                                width: 44,
                                height: 44,
                                border: hasUnread ? `2px solid ${theme.palette.primary.main}` : (mode === 'light' ? `2px solid ${alpha(theme.palette.grey[300], 0.5)}` : `2px solid ${alpha(theme.palette.common.white, 0.2)}`),
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {(c.name || '?').charAt(0)}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={c.name} 
                          secondary={c.preview} 
                          primaryTypographyProps={{ 
                            fontWeight: hasUnread ? 700 : 500,
                            color: hasUnread ? 'primary.main' : 'text.primary',
                          }} 
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Paper>

        {/* Main area: chatbox or empty state — full width on mobile */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            bgcolor: 'background.default',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          {withUserId ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={32} />
            </Box>
          ) : !currentConversation ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
                textAlign: 'center',
              }}
            >
              <Box>
                <ChatIcon sx={{ fontSize: 64, color: 'primary.main', opacity: 0.6, mb: 2 }} />
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Select a conversation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a chat from the list or start a new one from the friends list.
                </Typography>
                {!isDesktop && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                    Use the menu icon to open conversations, or the people icon to open friends and start a chat.
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <>
              <Paper
                elevation={0}
                sx={{
                  px: { xs: 1.5, md: 2.5 }, 
                  py: { xs: 1.25, md: 1.75 }, 
                  borderBottom: '1px solid', 
                  borderColor: 'divider', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  flexShrink: 0,
                  bgcolor: 'background.paper',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Avatar 
                  src={currentConversation.friend?.image} 
                  sx={{ 
                    width: { xs: 36, md: 40 }, 
                    height: { xs: 36, md: 40 },
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  {(currentConversation.friend?.name || '?').charAt(0)}
                </Avatar>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={700} 
                  sx={{ 
                    flex: 1,
                    fontSize: { xs: '0.95rem', md: '1rem' },
                  }}
                >
                  {currentConversation.friend?.name || 'Chat'}
                </Typography>
                <IconButton
                  onClick={handleConversationMenuOpen}
                  size="small"
                  aria-label="conversation options"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={conversationMenuAnchorEl}
                  open={Boolean(conversationMenuAnchorEl)}
                  onClose={handleConversationMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                      mt: 1.5,
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  }}
                >
                  <MenuItem onClick={handleViewProfile}>
                    <ListItemIcon>
                      <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    View Profile
                  </MenuItem>
                  <MenuItem onClick={handleDeleteConversation}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    Delete Conversation
                  </MenuItem>
                  <Divider />
                  {isBlocked ? (
                    <MenuItem onClick={handleUnblockUser}>
                      <ListItemIcon>
                        <CheckIcon fontSize="small" />
                      </ListItemIcon>
                      Unblock User
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={handleBlockUser}>
                      <ListItemIcon>
                        <BlockIcon fontSize="small" />
                      </ListItemIcon>
                      Block User
                    </MenuItem>
                  )}
                </Menu>
              </Paper>
              <Box 
                ref={messagesContainerRef}
                onScroll={handleScroll}
                sx={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  overflowX: 'hidden',
                  display: 'flex', 
                  flexDirection: 'column', 
                  p: { xs: 1.5, md: 2.5 },
                  minHeight: 0,
                  bgcolor: alpha(theme.palette.primary.main, 0.01),
                }}
              >
                {messagesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <>
                    {loadingOlderMessages && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                    {messages.map((msg) => {
                      const isMe = Number(msg.senderId) === myId;
                      const isImage = msg.messageType === 'image' && msg.filePath;
                      // Convert is_read to number (database may return as string "0" or "1" or number 0/1)
                      // Default to 0 (not read) if is_read is undefined/null
                      // is_read: 0 = sent (single checkmark), 1 = seen (double checkmark)
                      // Handle both string and number types from database
                      const isReadValue = msg.isRead !== undefined && msg.isRead !== null ? msg.isRead : 0;
                      const isReadStatus = typeof isReadValue === 'string' ? parseInt(isReadValue, 10) : Number(isReadValue);
                      const isRead = isReadStatus === 1;
                      // Construct image URL from file_path using configured uploads base (same as API origin, no /api)
                      const imageUrl = isImage 
                        ? (msg.filePath.startsWith('http') 
                            ? msg.filePath 
                            : getBookAssetUrl(msg.filePath))
                        : null;
                      
                      return (
                        <Box
                          key={msg.id}
                          data-message-id={msg.id}
                          sx={{
                            alignSelf: isMe ? 'flex-end' : 'flex-start',
                            maxWidth: { xs: '85%', md: '70%' },
                            mb: 1.5,
                            animation: 'fadeIn 0.3s ease',
                            '@keyframes fadeIn': {
                              from: {
                                opacity: 0,
                                transform: 'translateY(10px)',
                              },
                              to: {
                                opacity: 1,
                                transform: 'translateY(0)',
                              },
                            },
                          }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              px: isImage ? 0 : 2.5,
                              py: isImage ? 0 : 1.5,
                              borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              bgcolor: isImage ? 'transparent' : (isMe ? 'primary.main' : 'background.paper'),
                              color: isImage ? 'inherit' : (isMe ? 'primary.contrastText' : 'text.primary'),
                              overflow: 'hidden',
                              boxShadow: isImage ? 'none' : (isMe 
                                ? '0 1px 3px rgba(46, 125, 50, 0.12)' 
                                : '0 1px 3px rgba(0, 0, 0, 0.06)'),
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                boxShadow: isImage ? 'none' : (isMe 
                                  ? '0 2px 6px rgba(46, 125, 50, 0.15)' 
                                  : '0 2px 6px rgba(0, 0, 0, 0.08)'),
                              },
                            }}
                          >
                            {isImage && imageUrl ? (
                              <>
                                <Box
                                  component="img"
                                  src={imageUrl}
                                  alt="Sent image"
                                  sx={{
                                    maxWidth: '100%',
                                    maxHeight: '400px',
                                    display: 'block',
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      opacity: 0.9,
                                      transform: 'scale(1.01)',
                                    },
                                  }}
                                  onClick={() => window.open(imageUrl, '_blank')}
                                />
                                {msg.messageText && (
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word', px: 2, pt: 1 }}>
                                    {msg.messageText}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, px: 2, pb: 1 }}>
                                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                    {formatMessageTime(msg.createdAt)}
                                  </Typography>
                                  {isMe && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                                      {isRead ? (
                                        <DoneAllIcon sx={{ fontSize: 16, opacity: 0.9, color: 'primary.contrastText' }} />
                                      ) : (
                                        <DoneIcon sx={{ fontSize: 16, opacity: 0.7, color: 'primary.contrastText' }} />
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </>
                            ) : (
                              <>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    wordBreak: 'break-word',
                                    lineHeight: 1.5,
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {msg.messageText}
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'flex-end', 
                                  gap: 0.5, 
                                  mt: 1,
                                }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      opacity: 0.75,
                                      fontSize: '0.7rem',
                                    }}
                                  >
                                    {formatMessageTime(msg.createdAt)}
                                  </Typography>
                                  {isMe && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                                      {isRead ? (
                                        <DoneAllIcon sx={{ fontSize: 16, opacity: 0.95, color: 'primary.contrastText' }} />
                                      ) : (
                                        <DoneIcon sx={{ fontSize: 16, opacity: 0.8, color: 'primary.contrastText' }} />
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </>
                            )}
                          </Paper>
                        </Box>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>
              {!isBlocked && (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 1.5, md: 2 }, 
                    borderTop: '1px solid', 
                    borderColor: 'divider',
                    flexShrink: 0,
                    bgcolor: 'background.paper',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                  />
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                    <IconButton
                      color="primary"
                      onClick={handleImageButtonClick}
                      disabled={sendLoading || uploadingImage}
                      sx={{ 
                        flexShrink: 0,
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.15),
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                        borderRadius: 2,
                      }}
                      aria-label="Upload image"
                    >
                      {uploadingImage ? <CircularProgress size={24} /> : <ImageIcon />}
                    </IconButton>
                    <TextField
                      size="small"
                      placeholder="Type a message..."
                      fullWidth
                      multiline
                      maxRows={4}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: 3, 
                          bgcolor: mode === 'light' ? alpha(theme.palette.grey[100], 0.6) : alpha(theme.palette.common.white, 0.05),
                          color: 'text.primary',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: mode === 'light' ? alpha(theme.palette.grey[100], 0.8) : alpha(theme.palette.common.white, 0.08),
                          },
                          '&.Mui-focused': {
                            bgcolor: 'background.paper',
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                          },
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: mode === 'light' ? alpha(theme.palette.grey[300], 0.3) : alpha(theme.palette.common.white, 0.15),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: mode === 'light' ? alpha(theme.palette.grey[400], 0.5) : alpha(theme.palette.common.white, 0.25),
                        },
                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={() => handleSendMessage()}
                      disabled={(!messageText.trim() && !uploadingImage) || sendLoading || uploadingImage}
                      sx={{ 
                        flexShrink: 0,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          transform: 'scale(1.1)',
                        },
                        '&:disabled': {
                          bgcolor: alpha(theme.palette.action.disabled, 0.1),
                        },
                        transition: 'all 0.2s ease',
                        borderRadius: 2,
                      }}
                    >
                      {sendLoading ? <CircularProgress size={24} /> : <SendIcon />}
                    </IconButton>
                  </Box>
                </Paper>
              )}
              {isBlocked && (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    borderTop: '1px solid', 
                    borderColor: 'divider',
                    flexShrink: 0,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                  }}
                >
                  <Typography variant="body2" align="center" sx={{ mb: blockedByMe ? 1.5 : 0 }}>
                    {blockedByMe 
                      ? 'You have blocked this user. Messages cannot be sent.'
                      : 'This user has blocked you. Messages cannot be sent.'}
                  </Typography>
                  {blockedByMe && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleUnblockUser}
                        sx={{ textTransform: 'none' }}
                      >
                        Unblock User
                      </Button>
                    </Box>
                  )}
                </Paper>
              )}
            </>
          )}
        </Box>

        {/* Right column: Friend list (desktop only) */}
        {isDesktop && (
          <Paper
            elevation={0}
            sx={{
              width: 280,
              minWidth: 280,
              maxWidth: 280,
              borderLeft: '1px solid',
              borderColor: 'divider',
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, v) => setActiveTab(v)} 
                variant="fullWidth" 
                textColor="primary"
                indicatorColor="primary"
                sx={{ minHeight: 48 }}
              >
                <Tab label="Friends" sx={{ minWidth: 0, px: 1, fontSize: '0.8rem', textTransform: 'none' }} />
                <Tab 
                  label={
                    <Badge badgeContent={requests.length} color="error" invisible={requests.length === 0} sx={{ '& .MuiBadge-badge': { right: -10, top: -5 } }}>
                      Requests
                    </Badge>
                  } 
                  sx={{ minWidth: 0, px: 1, fontSize: '0.8rem', textTransform: 'none' }} 
                />
                <Tab label="Find" sx={{ minWidth: 0, px: 1, fontSize: '0.8rem', textTransform: 'none' }} />
              </Tabs>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
              {activeTab === 0 && (
                <>
                  {friendsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : !hasFriends ? (
                    <Box sx={{ 
                      p: 4, 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.6 }} />
                      </Box>
                      <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
                        No friends yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                        Add friends from their profile to chat
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <List disablePadding>
                        {friends.map((f) => (
                          <ListItem key={f.userId || f.phone} disablePadding>
                            <ListItemButton 
                              onClick={() => handleSelectFriend(f.userId || f.phone)}
                              sx={{
                                px: 2,
                                py: 1.5,
                                borderRadius: 0,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  transform: 'translateX(2px)',
                                },
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 48 }}>
                                <Avatar 
                                  src={f.userImage} 
                                  alt={f.userName}
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    border: `2px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                  }}
                                >
                                  {(f.userName || '?').charAt(0)}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText 
                                primary={f.userName} 
                                primaryTypographyProps={{ 
                                  fontWeight: 500,
                                  fontSize: '0.95rem',
                                }} 
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                      {friendsHasMore && (
                        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            size="small"
                            onClick={handleLoadMoreFriends}
                            disabled={friendsLoadingMore}
                            sx={{ textTransform: 'none' }}
                          >
                            {friendsLoadingMore ? <CircularProgress size={20} /> : 'Load more'}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

              {activeTab === 1 && (
                <List disablePadding>
                  {requestsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                  ) : requests.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">No new requests</Typography>
                    </Box>
                  ) : (
                    requests.map((r) => (
                      <Box key={r.requestId || r.userId} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <ListItem disablePadding>
                          <ListItemButton sx={{ px: 2, py: 1.5, cursor: 'default' }}>
                            <ListItemAvatar>
                              <Avatar src={r.userImage} alt={r.userName}>{(r.userName || '?').charAt(0)}</Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                              primary={r.userName} 
                              secondary={r.createdAt ? formatTimeAgo(new Date(r.createdAt).getTime()/1000) : null}
                              primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                              secondaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                          </ListItemButton>
                        </ListItem>
                        <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1 }}>
                          <Button 
                            fullWidth 
                            variant="contained" 
                            size="small" 
                            onClick={() => handleAcceptRequest(r.userId)}
                            startIcon={<CheckIcon />}
                            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                          >
                            Confirm
                          </Button>
                          <Button 
                            fullWidth 
                            variant="outlined" 
                            size="small" 
                            color="inherit"
                            onClick={() => handleDeclineRequest(r.userId)}
                            startIcon={<CloseIcon />}
                            sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    ))
                  )}
                </List>
              )}

              {activeTab === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ p: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search phone or name..."
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <PersonIcon fontSize="small" />
                          </ListItemIcon>
                        ),
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {searchLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                    ) : searchQuery ? (
                      searchResults.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">No users found</Typography>
                        </Box>
                      ) : (
                        <List disablePadding>
                          {searchResults.map((user) => renderSearchResultItem(user))}
                        </List>
                      )
                    ) : (
                      // Default View: People You May Know
                      <List disablePadding>
                        {suggestionsLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                        ) : suggestions.length === 0 ? (
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">No suggestions available</Typography>
                          </Box>
                        ) : (
                          suggestions.map((s) => (
                            <ListItem 
                              key={s.userId} 
                              disablePadding
                              secondaryAction={
                                <IconButton edge="end" color="primary" onClick={() => handleAddFriend(s.phone)} size="small">
                                  <PersonAddIcon />
                                </IconButton>
                              }
                            >
                              <ListItemButton sx={{ px: 2, py: 1.5, cursor: 'default' }}>
                                <ListItemAvatar>
                                  <Avatar src={s.userImage} alt={s.userName}>{(s.userName || '?').charAt(0)}</Avatar>
                                </ListItemAvatar>
                                <ListItemText 
                                  primary={s.userName} 
                                  secondary={s.mutualFriends ? `${s.mutualFriends} mutual friends` : null}
                                  primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))
                        )}
                      </List>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Mobile: Left drawer = conversation list */}
      {!isDesktop && (
        <Drawer
          anchor="left"
          open={mobileConversationsDrawerOpen}
          onClose={() => setMobileConversationsDrawerOpen(false)}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
          PaperProps={{
            sx: { 
              width: 300, 
              maxWidth: '85vw',
              mt: { xs: '56px', sm: '60px' }, // AppBar height
              height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 60px)' },
            },
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            p: 2, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}>
            <Typography fontWeight={700} variant="subtitle1">Conversations</Typography>
            <IconButton 
              onClick={() => setMobileConversationsDrawerOpen(false)} 
              size="small" 
              aria-label="Close"
              sx={{
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.1),
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {conversationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} />
              </Box>
            ) : !hasConversations ? (
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <MessageIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.6 }} />
                </Box>
                <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ mb: 0.5 }}>
                  No conversations yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                  Open Friends to start a chat
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {conversations.map((c) => {
                  const hasUnread = (c.unreadCount || 0) > 0;
                  return (
                    <ListItem key={c.id} disablePadding>
                      <ListItemButton
                        selected={currentConversation?.id === c.id}
                        onClick={() => handleSelectConversation(c)}
                        sx={{
                          bgcolor: hasUnread && currentConversation?.id !== c.id 
                            ? alpha(theme.palette.primary.main, 0.08) 
                            : 'transparent',
                          '&:hover': {
                            bgcolor: hasUnread && currentConversation?.id !== c.id
                              ? alpha(theme.palette.primary.main, 0.12)
                              : alpha(theme.palette.action.hover, 0.05),
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Badge 
                            badgeContent={hasUnread ? c.unreadCount : 0} 
                            color="primary" 
                            max={99}
                            invisible={!hasUnread}
                          >
                            <Avatar src={c.image} alt={c.name}>{(c.name || '?').charAt(0)}</Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={c.name} 
                          secondary={c.preview} 
                          primaryTypographyProps={{ 
                            fontWeight: hasUnread ? 700 : 500,
                            color: hasUnread ? 'primary.main' : 'text.primary',
                          }} 
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </Drawer>
      )}

      {/* Mobile: Right drawer = friend list */}
      {!isDesktop && (
        <Drawer
          anchor="right"
          open={mobileFriendsDrawerOpen}
          onClose={() => setMobileFriendsDrawerOpen(false)}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
          PaperProps={{
            sx: {
              width: 300,
              maxWidth: '85vw',
              mt: { xs: '56px', sm: '60px' }, // AppBar height
              height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 60px)' },
            },
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Tabs 
                value={activeTab} 
                onChange={(e, v) => setActiveTab(v)} 
                variant="fullWidth" 
                textColor="primary"
                indicatorColor="primary"
                sx={{ minHeight: 40 }}
              >
                <Tab label="Friends" sx={{ minWidth: 0, px: 1, fontSize: '0.8rem', minHeight: 40, textTransform: 'none' }} />
                <Tab 
                  label={
                    <Badge badgeContent={requests.length} color="error" invisible={requests.length === 0} sx={{ '& .MuiBadge-badge': { right: -10, top: -5 } }}>
                      Requests
                    </Badge>
                  } 
                  sx={{ minWidth: 0, px: 1, fontSize: '0.8rem', minHeight: 40, textTransform: 'none' }} 
                />
                <Tab label="Find" sx={{ minWidth: 0, px: 1, fontSize: '0.8rem', minHeight: 40, textTransform: 'none' }} />
              </Tabs>
            </Box>
            <IconButton 
              onClick={() => setMobileFriendsDrawerOpen(false)} 
              size="small" 
              aria-label="Close"
              sx={{ ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ overflowY: 'auto', flex: 1 }}>
            {activeTab === 0 && (
              <>
                {friendsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : !hasFriends ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No friends yet. Add friends from their profile to chat.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <List disablePadding>
                      {friends.map((f) => (
                        <ListItem key={f.userId || f.phone} disablePadding>
                          <ListItemButton onClick={() => handleSelectFriend(f.userId || f.phone)}>
                            <ListItemAvatar>
                              <Avatar src={f.userImage} alt={f.userName}>
                                {(f.userName || '?').charAt(0)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={f.userName} primaryTypographyProps={{ fontWeight: 500 }} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                    {friendsHasMore && (
                      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          size="small"
                          onClick={handleLoadMoreFriends}
                          disabled={friendsLoadingMore}
                          sx={{ textTransform: 'none' }}
                        >
                          {friendsLoadingMore ? <CircularProgress size={20} /> : 'Load more'}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}

            {activeTab === 1 && (
              <List disablePadding>
                {requestsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                ) : requests.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">No new requests</Typography>
                  </Box>
                ) : (
                  requests.map((r) => (
                    <Box key={r.requestId || r.userId} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <ListItem disablePadding>
                        <ListItemButton sx={{ px: 2, py: 1.5, cursor: 'default' }}>
                          <ListItemAvatar>
                            <Avatar src={r.userImage} alt={r.userName}>{(r.userName || '?').charAt(0)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                            primary={r.userName} 
                            secondary={r.createdAt ? formatTimeAgo(new Date(r.createdAt).getTime()/1000) : null}
                            primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                      <Box sx={{ px: 2, pb: 1.5, display: 'flex', gap: 1 }}>
                        <Button 
                          fullWidth 
                          variant="contained" 
                          size="small" 
                          onClick={() => handleAcceptRequest(r.userId)}
                          startIcon={<CheckIcon />}
                          sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                        >
                          Confirm
                        </Button>
                        <Button 
                          fullWidth 
                          variant="outlined" 
                          size="small" 
                          color="inherit"
                          onClick={() => handleDeclineRequest(r.userId)}
                          startIcon={<CloseIcon />}
                          sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  ))
                )}
              </List>
            )}

            {activeTab === 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ p: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search phone or name..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <PersonIcon fontSize="small" />
                        </ListItemIcon>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {searchLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                  ) : searchQuery ? (
                    searchResults.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">No users found</Typography>
                      </Box>
                    ) : (
                      <List disablePadding>
                        {searchResults.map((user) => renderSearchResultItem(user))}
                      </List>
                    )
                  ) : (
                    // Default View: People You May Know
                    <List disablePadding>
                      {suggestionsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={32} /></Box>
                      ) : suggestions.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">No suggestions available</Typography>
                        </Box>
                      ) : (
                        suggestions.map((s) => (
                          <ListItem 
                            key={s.userId} 
                            disablePadding
                            secondaryAction={
                              <IconButton edge="end" color="primary" onClick={() => handleAddFriend(s.phone)} size="small">
                                <PersonAddIcon />
                              </IconButton>
                            }
                          >
                            <ListItemButton sx={{ px: 2, py: 1.5, cursor: 'default' }}>
                              <ListItemAvatar>
                                <Avatar src={s.userImage} alt={s.userName}>{(s.userName || '?').charAt(0)}</Avatar>
                              </ListItemAvatar>
                              <ListItemText 
                                primary={s.userName} 
                                secondary={s.mutualFriends ? `${s.mutualFriends} mutual friends` : null}
                                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))
                      )}
                    </List>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Drawer>
      )}

      {/* Conversation Menu */}
      <Menu
        anchorEl={conversationMenuAnchorEl}
        open={Boolean(conversationMenuAnchorEl)}
        onClose={handleConversationMenuClose}
        onClick={handleConversationMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewProfile}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          View Profile
        </MenuItem>
        <MenuItem onClick={handleDeleteConversation}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete Conversation
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleBlockUser} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <BlockIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Block
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Chat;
